// client/src/components/BiometricLockGate.tsx — OTT 2026
//
// Overlay di sblocco con impronta/Face ID: quando l'utente ha attivato lo
// sblocco biometrico su questo device E la sessione non è attiva, all'avvio
// dell'app appare questa schermata al posto del login. Un tocco (o il
// tentativo automatico) → biometria → nuova sessione dal device fidato.
//
// Fallback SEMPRE presente: "Usa la password" chiude l'overlay per l'intera
// sessione dell'app (sessionStorage) e lascia il flusso di login normale.
// Il codice di lancio è coerente con le app bancarie: la biometria non
// sostituisce mai il 2FA sui device nuovi, sblocca solo questo.

import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Fingerprint, Loader2, KeyRound } from "lucide-react";

import useAuthStore from "@/store/useAuthStore";
import {
  isBiometricUnlockEnabled,
  getBiometricAvailability,
  tryBiometricLogin,
} from "@/services/biometricAuth";

/** Skip per la sessione corrente dell'app (l'utente sceglie la password). */
const SKIP_KEY = "rc_biometric_skip_session";

export default function BiometricLockGate() {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((s) => !!s.accessToken);
  const isHydrated = useAuthStore((s) => s.isHydrated);

  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [label, setLabel] = useState("Biometria");
  const autoAttemptedRef = useRef(false);

  // Decide se mostrare il gate: solo dopo l'idratazione della sessione,
  // solo se NON autenticato e solo se lo sblocco è attivo su questo device.
  useEffect(() => {
    if (isAuthenticated) {
      setShow(false);
      return;
    }
    if (isHydrated === false) return; // sessione ancora in caricamento
    if (sessionStorage.getItem(SKIP_KEY) === "1") return;

    let cancelled = false;
    (async () => {
      const enabled = await isBiometricUnlockEnabled();
      if (cancelled || !enabled) return;
      const avail = await getBiometricAvailability();
      if (cancelled || !avail.available) return;
      setLabel(avail.label || "Biometria");
      setShow(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isHydrated]);

  const skip = () => {
    sessionStorage.setItem(SKIP_KEY, "1");
    setShow(false);
  };

  const unlock = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    const res = await tryBiometricLogin();
    if (res.status === "ok") {
      useAuthStore.getState().setSession(res.user, res.accessToken);
      setShow(false);
      navigate("/dashboard", { replace: true });
    } else if (res.status === "invalid") {
      setError(
        "Sblocco non più valido su questo dispositivo: accedi con la password.",
      );
      setTimeout(skip, 2200);
    } else if (res.status === "conflict") {
      setError(
        "C'è un altro accesso attivo su un altro dispositivo: accedi con la password per gestirlo.",
      );
      setTimeout(skip, 2600);
    } else if (res.status === "unavailable") {
      skip();
    }
    // "cancelled": resta l'overlay, l'utente può riprovare o usare la password
    setBusy(false);
  };

  // Tentativo automatico al primo render dell'overlay (stile app bancarie).
  useEffect(() => {
    if (show && !autoAttemptedRef.current) {
      autoAttemptedRef.current = true;
      void unlock();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  if (!show || isAuthenticated) return null;

  return (
    <div
      className="fixed inset-0 z-[80] bg-neutral-950 flex items-center justify-center px-6"
      role="dialog"
      aria-label="Sblocco biometrico"
    >
      <div className="w-full max-w-sm text-center space-y-6">
        <div className="mx-auto w-20 h-20 rounded-3xl bg-brand-500/15 border border-brand-500/30 flex items-center justify-center">
          <Fingerprint size={40} className="text-brand-400" />
        </div>
        <div className="space-y-2">
          <h1 className="text-xl font-bold text-white">Sblocca RideConnect</h1>
          <p className="text-sm text-neutral-400">
            Tocca il pulsante e usa {label} per entrare.
          </p>
        </div>

        {error && (
          <p className="text-sm text-amber-400" role="alert">
            {error}
          </p>
        )}

        <div className="space-y-3">
          <button
            type="button"
            onClick={unlock}
            disabled={busy}
            className="w-full py-3.5 rounded-xl bg-brand-500 hover:bg-brand-400 disabled:opacity-60 text-white font-bold text-sm flex items-center justify-center gap-2"
          >
            {busy ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <Fingerprint size={18} />
            )}
            Sblocca con {label}
          </button>
          <button
            type="button"
            onClick={skip}
            disabled={busy}
            className="w-full py-3 rounded-xl border border-neutral-800 text-neutral-400 hover:text-neutral-200 hover:border-neutral-600 text-sm flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <KeyRound size={16} />
            Usa la password
          </button>
        </div>
      </div>
    </div>
  );
}
